import { useEffect, useState, type ReactNode } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { ErrorBoundary } from "./ErrorBoundary";
import { useApiState } from "../lib/useApiState";
import { apiFetch } from "../lib/api";
import { MobileHeader } from "./MobileHeader";

type Props = {
  /** Map of pathname -> page title for accessibility announcements and document.title */
  routeTitles: Record<string, string>;
  /** Bottom tab bar component (app-specific, handles feature gating) */
  tabBar: (unreadCount: number) => ReactNode;
  /** Extra elements to render at the bottom of the shell (walkthrough overlay, modals, etc.) */
  children?: ReactNode;
};

export function MobileShell({ routeTitles, tabBar, children }: Props) {
  const location = useLocation();
  const [announcement, setAnnouncement] = useState("");

  const unreadState = useApiState(async (signal) => {
    const res = await apiFetch<{ unread_count: number }>("/api/v1/messages/unread_count", { signal });
    return res.data.unread_count;
  }, []);

  // Refresh unread badge when a conversation is marked as read
  useEffect(() => {
    const handler = () => unreadState.reload();
    window.addEventListener("messages:unread-changed", handler);
    return () => window.removeEventListener("messages:unread-changed", handler);
  }, [unreadState.reload]);

  // Announce route changes for screen readers
  useEffect(() => {
    const title = routeTitles[location.pathname] || "Page";
    setAnnouncement(`Navigated to ${title}`);
    document.title = `${title} - cradleOS`;
  }, [location.pathname, routeTitles]);

  return (
    <div className="flex bg-bg h-screen flex-col overflow-hidden mx-auto w-full relative">
      {/* Skip to content link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-[100] focus:rounded-md focus:bg-accent focus:px-4 focus:py-2 focus:text-white focus:shadow-lg"
      >
        Skip to content
      </a>

      {/* Route change announcer (screen-reader only) */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>

      <MobileHeader />

      <main id="main-content" className="flex-1 overflow-y-auto px-5 pb-[calc(8rem+env(safe-area-inset-bottom,0px))] pt-5">
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>

      {tabBar(unreadState.data ?? 0)}
      {children}
    </div>
  );
}
